/**
 * O campo de EPI de uma linha da entrega. O catálogo tem dezenas de milhares de C.A. (a base do
 * MTE), então a busca vai ao servidor conforme se digita — por nome, fabricante ou número do C.A.
 * — em vez de carregar a lista inteira.
 *
 * `epi.read` é permissão própria, diferente da porta da tela: passa por `useListaPermitida`, que
 * diz por que o campo ficou vazio em vez de mostrar erro.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Autocomplete, Box, TextField, Typography } from '@mui/material';

import { useListaPermitida } from '../../../authorization/useListaPermitida';
import epiService from '../../../services/epiService';
import { EpiStatus, type Epi } from '../../../types/epi';
import { dataDaEntrega } from '../../../types/entregaDeEpi';

const MINIMO_PARA_BUSCAR = 2;

export interface CampoDeEpiProps {
  valor: Epi | null;
  onChange: (epi: Epi | null) => void;
  /** Data da entrega, para avisar C.A. vencido; `null` na devolução, que pode devolver vencido. */
  conferirValidadeEm: string | null;
}

function caVencido(epi: Epi, data: string): boolean {
  return Boolean(epi.fimValidade) && epi.fimValidade.slice(0, 10) < data;
}

export const CampoDeEpi: React.FC<CampoDeEpiProps> = ({ valor, onChange, conferirValidadeEm }) => {
  const [texto, setTexto] = useState('');
  const buscaAtual = useRef('');

  const carregar = useCallback(async (): Promise<Epi[]> => {
    if (buscaAtual.current.length < MINIMO_PARA_BUSCAR) return [];
    const resposta = await epiService.getEpis({ page: 1, limit: 30, search: buscaAtual.current, ativo: EpiStatus.ATIVO });
    return resposta.epis;
  }, []);
  const epis = useListaPermitida('epi.read', 'EPI', carregar);

  const { recarregar } = epis;
  useEffect(() => {
    const termo = texto.trim();
    // Escolher uma opção preenche o texto com o rótulo dela; isso não é busca nova.
    if (valor && texto === rotulo(valor)) return;
    if (termo === buscaAtual.current) return;
    const espera = window.setTimeout(() => {
      buscaAtual.current = termo;
      recarregar();
    }, 350);
    return () => window.clearTimeout(espera);
  }, [texto, valor, recarregar]);

  const vencido = valor !== null && conferirValidadeEm !== null && caVencido(valor, conferirValidadeEm);
  const dica = valor
    ? vencido
      ? `C.A. vencido em ${dataDaEntrega(valor.fimValidade)} — a NR-6 não permite entregar`
      : `C.A. válido até ${dataDaEntrega(valor.fimValidade)}`
    : (epis.bloqueio ?? epis.erro ?? 'Digite o nome, o fabricante ou o número do C.A.');

  return (
    <Autocomplete
      sx={{ flex: 1, minWidth: 0 }}
      options={epis.itens as Epi[]}
      value={valor}
      onChange={(_, novo) => onChange(novo)}
      inputValue={texto}
      onInputChange={(_, novo) => setTexto(novo)}
      filterOptions={(opcoes) => opcoes}
      getOptionLabel={rotulo}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      loading={epis.carregando}
      disabled={!epis.permitido}
      noOptionsText={
        texto.trim().length < MINIMO_PARA_BUSCAR ? 'Digite ao menos 2 letras ou o número do C.A.' : 'Nenhum EPI ativo com esse nome ou C.A. no catálogo'
      }
      renderOption={(props, e) => (
        <li {...props} key={e.id}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2">{e.nome}</Typography>
            <Typography variant="caption" color="text.secondary">
              C.A. {e.metodo} · válido até {dataDaEntrega(e.fimValidade)}
              {e.brandModel ? ` · ${e.brandModel}` : ''}
            </Typography>
          </Box>
        </li>
      )}
      renderInput={(params) => (
        <TextField {...params} label="EPI" size="small" error={vencido || Boolean(epis.erro)} helperText={dica} />
      )}
    />
  );
};

function rotulo(e: Epi): string {
  return `${e.nome} — C.A. ${e.metodo}`;
}

export default CampoDeEpi;
